import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import SectionCard from './SectionCard';
import KpiStat from './KpiStat';
import StatusBar, { statusBarItems } from './StatusBar';
import useModuleStats from './useModuleStats';
import RecentItems from './RecentItems';
import { documentStatusLabels } from './statusLabels';
import { formatCount } from '../overview/format';

/** Documents — pipeline distribution draft→signed. */
const DocumentsSection: React.FC = () => {
  const { isRTL } = useLanguage();
  const { data, loading, error } = useModuleStats<any>('/documents/statistics/');

  if (error) return null;
  // No records yet: keep the module visible with an empty state rather than hiding it.
  const empty = !loading && (!data || (data.total ?? 0) === 0)
    ? {
        title: isRTL ? 'لا توجد مستندات حتى الآن' : 'No documents yet',
        text: isRTL ? 'ستظهر هنا حالة المستندات من المسودة حتى التوقيع بمجرد إنشاء أول مستند.' : 'Document status from draft to signed will appear here once the first document is created.',
        action: { to: '/documents/create', label: isRTL ? 'إنشاء مستند' : 'Create document' },
      }
    : null;

  const byStatus: Record<string, number> = {
    draft: data?.draft,
    in_progress: data?.in_progress,
    under_review: data?.under_review,
    approved: data?.approved,
    sent_to_client: data?.sent_to_client,
    signed: data?.signed,
    archived: data?.archived,
  };

  return (
    <SectionCard
      icon="bi-file-earmark-richtext"
      title={isRTL ? 'المستندات' : 'Documents'}
      caption={isRTL ? 'من المسودة حتى التوقيع' : 'From draft to signed'}
      to="/documents"
      loading={loading}
      empty={empty}
      stats={(
        <>
          <KpiStat value={formatCount(data?.total)} label={isRTL ? 'الإجمالي' : 'Total'} />
          <KpiStat value={formatCount(data?.under_review)} label={isRTL ? 'قيد المراجعة' : 'Under Review'} tone="primary" />
          <KpiStat value={formatCount(data?.signed)} label={isRTL ? 'موقّعة' : 'Signed'} tone="success" />
          <KpiStat value={formatCount(data?.templates)} label={isRTL ? 'القوالب' : 'Templates'} />
        </>
      )}
    >
      <StatusBar items={statusBarItems(byStatus, documentStatusLabels(isRTL))} />
      <RecentItems
        endpoint="/documents/"
        title={isRTL ? 'أحدث المستندات' : 'Recent Documents'}
        itemLink={(d) => `/documents/${d.id}`}
        primary={(d) => (isRTL && d.title_ar ? d.title_ar : d.title)}
        secondary={(d) => d.document_id}
        statusKey={(d) => d.status}
        labels={documentStatusLabels(isRTL)}
      />
    </SectionCard>
  );
};

export default DocumentsSection;
